import { useEffect, useState } from "react";
import { Save } from "lucide-react";

export const BOOKSTORE_STATUSES = ["not_contacted", "contacted", "follow_up_needed", "interested", "reviewing", "accepted", "declined", "no_response"];
export const statusLabel = value => String(value || "").replaceAll("_", " ");
export const displayDate = value => value && !String(value).startsWith("0000") ? new Date(value).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "-";
export function localDateInput(value) {
  if (!value || String(value).startsWith("0000")) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
export const isoDate = value => value ? new Date(value).toISOString() : null;

const fields = [
  ["store_name", "Bookstore name", "text", 255], ["contact_name", "Contact name", "text", 255],
  ["email", "Email", "email", 255], ["phone", "Phone", "tel", 10],
  ["website", "Website", "url", 1000], ["address", "Street address", "text", 255],
  ["city", "City", "text", 100], ["state", "State / Province", "text", 100],
  ["postal_code", "Postal code", "text", 30], ["country", "Country", "text", 100],
];

function formValues(store) {
  return {
    ...Object.fromEntries(fields.map(([key]) => [key, store[key] || ""])),
    status: store.status || "not_contacted", notes: store.notes || "",
    books_offered: store.books_offered || [],
    contacted_at: localDateInput(store.contacted_at), last_contacted_at: localDateInput(store.last_contacted_at),
    responded_at: localDateInput(store.responded_at), follow_up_at: localDateInput(store.follow_up_at),
  };
}

export default function BookstoreForm({ bookstore = {}, books = [], onSave, saving, onDirty, submitLabel = "Save bookstore" }) {
  const [values, setValues] = useState(() => formValues(bookstore));
  useEffect(() => { setValues(formValues(bookstore)); }, [bookstore]);

  const change = (key, value) => { setValues(current => ({ ...current, [key]: value })); onDirty?.(true); };
  const toggleBook = title => change("books_offered", values.books_offered.includes(title) ? values.books_offered.filter(book => book !== title) : [...values.books_offered, title]);
  const submit = event => {
    event.preventDefault();
    onSave({ ...values, ...Object.fromEntries(["contacted_at", "last_contacted_at", "responded_at", "follow_up_at"].map(key => [key, isoDate(values[key])])) });
  };

  return <form className="bookstore-form" onSubmit={submit}>
    <fieldset disabled={saving}>
      <div className="bookstore-fields">
        {fields.map(([key, label, type, max]) => <label key={key}>{label}
          <input name={key} type={type} maxLength={max} inputMode={key === "phone" ? "numeric" : undefined} pattern={key === "phone" ? "[0-9]{1,10}" : undefined} required={key === "store_name"} value={values[key]} onChange={event => change(key, key === "phone" ? event.target.value.replace(/\D/g, "").slice(0, 10) : event.target.value)} />
        </label>)}
        <label>Status<select name="status" value={values.status} onChange={event => change("status", event.target.value)}>
          {BOOKSTORE_STATUSES.map(status => <option key={status} value={status}>{statusLabel(status)}</option>)}
        </select></label>
        <label>Follow-up date<input name="follow_up_at" type="datetime-local" value={values.follow_up_at} onChange={event => change("follow_up_at", event.target.value)} /></label>
        <label>First contact<input name="contacted_at" type="datetime-local" value={values.contacted_at} onChange={event => change("contacted_at", event.target.value)} /></label>
        <label>Last response<input name="responded_at" type="datetime-local" value={values.responded_at} onChange={event => change("responded_at", event.target.value)} /></label>
      </div>
      <fieldset className="bookstore-books"><legend>Books offered</legend>
        {books.length ? books.map(book => <label key={book.title} className="bookstore-book-option">
          <input type="checkbox" checked={values.books_offered.includes(book.title)} onChange={() => toggleBook(book.title)} />
          {book.image && <img src={book.image} alt="" loading="lazy" />}
          <span>{book.title}</span>
        </label>) : <p>No catalog books available.</p>}
      </fieldset>
      <label>Notes<textarea name="notes" maxLength={20000} rows={4} value={values.notes} onChange={event => change("notes", event.target.value)} /></label>
    </fieldset>
    <div className="bookstore-actions"><button type="submit" className="bookstore-primary" disabled={saving}><Save size={17} />{saving ? "Saving..." : submitLabel}</button></div>
  </form>;
}
